import type { IconName } from '@/components/Icon'
import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'
import { serviceTypeIcons } from '@/features/dashboard/service-type-icons'

import taxFilingStatusLight from '@/assets/misc/tax_filing_status_light.png'
import taxFilingStatusLightMobile from '@/assets/misc/tax_filing_status_light_mobile.png'
import taxFilingStatusDark from '@/assets/misc/tax_filing_status_dark.png'
import taxFilingStatusDarkMobile from '@/assets/misc/tax_filing_status_dark_mobile.png'

import infoGatheringLight from '@/assets/misc/info_gathering_checklist_light.png'
import infoGatheringLightMobile from '@/assets/misc/info_gathering_checklist_light_mobile.png'
import infoGatheringDark from '@/assets/misc/info_gathering_checklist_dark.jpg'
import infoGatheringDarkMobile from '@/assets/misc/info_gathering_checklist_dark_mobile.jpg'

import advisoryCalendarLight from '@/assets/misc/advisory_calendar_light.png'
import advisoryCalendarLightMobile from '@/assets/misc/advisory_calendar_light_mobile.png'
import advisoryCalendarDark from '@/assets/misc/advisory_calendar_dark.png'
import advisoryCalendarDarkMobile from '@/assets/misc/advisory_calendar_dark_mobile.png'

import bookkeepingLight from '@/assets/misc/bookkeeping_light.png'
import bookkeepingLightMobile from '@/assets/misc/bookkeeping_light_mobile.png'
import bookkeepingDark from '@/assets/misc/bookkeeping_dark.png'
import bookkeepingDarkMobile from '@/assets/misc/bookkeeping_dark_mobile.png'

import checkoutPageLight from '@/assets/misc/checkout_page_light.png'
import checkoutPageLightMobile from '@/assets/misc/checkout_page_light_mobile.png'
import checkoutPageDark from '@/assets/misc/checkout_page_dark.png'
import checkoutPageDarkMobile from '@/assets/misc/checkout_page_dark_mobile.png'

import jobChatLight from '@/assets/misc/job_chat_light.png'
import jobChatLightMobile from '@/assets/misc/job_chat_light_mobile.png'
import jobChatDark from '@/assets/misc/job_chat_dark.jpg'
import jobChatDarkMobile from '@/assets/misc/job_chat_dark_mobile.jpg'

export interface ImageSet {
  light: string
  lightMobile: string
  dark: string
  darkMobile: string
}

export interface ServiceTypeInfoCard {
  title: string
  description: string
  images: ImageSet
}

export interface ServiceTypeInfoContent {
  displayTitle: string
  icon: IconName
  firstCard: ServiceTypeInfoCard
  middleCard: ServiceTypeInfoCard
  lastCard: ServiceTypeInfoCard
}

const sharedFirstCard: ServiceTypeInfoCard = {
  title: 'Start with a few questions, then complete check out',
  description:
    'Tell us about your tax situation so we can tailor the process to your needs.',
  images: {
    light: taxFilingStatusLight,
    lightMobile: taxFilingStatusLightMobile,
    dark: taxFilingStatusDark,
    darkMobile: taxFilingStatusDarkMobile,
  },
}

const sharedLastCard: ServiceTypeInfoCard = {
  title: 'Finally, get matched with a licensed tax pro',
  description:
    'An expert will handle your return and contact you if anything is missing.',
  images: {
    light: jobChatLight,
    lightMobile: jobChatLightMobile,
    dark: jobChatDark,
    darkMobile: jobChatDarkMobile,
  },
}

export const serviceTypeInfoContentMap: Record<
  LegendServiceType,
  ServiceTypeInfoContent
> = {
  Tax: {
    displayTitle: 'Tax Filing',
    icon: serviceTypeIcons.Tax,
    firstCard: sharedFirstCard,
    middleCard: {
      title: 'Then, upload your info and documents',
      description:
        "We'll guide you through submitting all the personal details and tax files.",
      images: {
        light: infoGatheringLight,
        lightMobile: infoGatheringLightMobile,
        dark: infoGatheringDark,
        darkMobile: infoGatheringDarkMobile,
      },
    },
    lastCard: sharedLastCard,
  },
  Consultation: {
    displayTitle: 'Advisory',
    icon: serviceTypeIcons.Consultation,
    firstCard: sharedFirstCard,
    middleCard: {
      title: 'Then, schedule your advisory session',
      description:
        "Pick a time that works for you and we'll connect you with a qualified professional.",
      images: {
        light: advisoryCalendarLight,
        lightMobile: advisoryCalendarLightMobile,
        dark: advisoryCalendarDark,
        darkMobile: advisoryCalendarDarkMobile,
      },
    },
    lastCard: sharedLastCard,
  },
  Bookkeeping: {
    displayTitle: 'Bookkeeping',
    icon: serviceTypeIcons.Bookkeeping,
    firstCard: sharedFirstCard,
    middleCard: {
      title: 'Then, share your financial records',
      description:
        'Upload your receipts, statements, and other financial documents for review.',
      images: {
        light: bookkeepingLight,
        lightMobile: bookkeepingLightMobile,
        dark: bookkeepingDark,
        darkMobile: bookkeepingDarkMobile,
      },
    },
    lastCard: sharedLastCard,
  },
  Misc: {
    displayTitle: 'Other Tax Services',
    icon: serviceTypeIcons.Misc,
    firstCard: sharedFirstCard,
    middleCard: {
      title: 'Then, provide any additional details',
      description:
        'Share the relevant information so we can match you with the right service.',
      images: {
        light: checkoutPageLight,
        lightMobile: checkoutPageLightMobile,
        dark: checkoutPageDark,
        darkMobile: checkoutPageDarkMobile,
      },
    },
    lastCard: sharedLastCard,
  },
}
